import { PropTypes } from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Button from '@zenbusiness/zui/Button';
import VisuallyHidden from '@zenbusiness/zui/VisuallyHidden';

import ContactInfoFieldset from '#client/components/ContactInfoFieldset';
import CtaContainer from '#client/components/CtaContainer';
import Form from '#client/components/Form';
import { formErrorsToTrackingErrorsMapper } from '#client/utils/formErrorsToTrackingErrorsMapper';

const initialValues = {
  email: '',
  firstName: '',
  lastName: '',
  phone: ''
};

function ContactInfoForm({ defaultValues = initialValues, onSkip, onSubmit } = {}) {
  const captureException = useCaptureException();
  const trackFormSubmitted = useClickstreamCallback('formSubmitted');
  const trackFormSubmissionFailed = useClickstreamCallback('formSubmissionFailed');

  const formMethods = useForm({ defaultValues });

  const submitHandler = (data) => {
    onSubmit(data);
  };

  const submitErrorHandler = (_errors) => {
    trackFormSubmissionFailed({ formErrors: formErrorsToTrackingErrorsMapper(_errors) });
  };

  const handleFormSubmit = (e) => {
    trackFormSubmitted({ elementName: 'contact_info_form' });
    formMethods.handleSubmit(submitHandler, submitErrorHandler)(e).catch(captureException);
  };

  const { isSubmitting, errors } = formMethods.formState;
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <FormProvider {...formMethods}>
      <Form aria-label="Contact information" spacing={2} onSubmit={handleFormSubmit}>
        <VisuallyHidden as="legend">Contact Information</VisuallyHidden>

        <ContactInfoFieldset />

        <CtaContainer marginBlockStart={0}>
          <Button
            clickstreamProps={{
              elementName: 'contact_info_form_submit'
            }}
            color="primary"
            data-testid="contact-info-form-submit"
            disabled={hasErrors}
            fullWidth
            loading={isSubmitting}
            type="submit"
            variant="contained"
          >
            <FormattedMessage id="shopping.page.contactInfo.form.submitButton" />
          </Button>

          {onSkip && (
            <Button
              clickstreamProps={{
                elementName: 'contact_info_form_skip'
              }}
              data-testid="contact-info-form-skip"
              fullWidth
              variant="outlined"
              onClick={onSkip}
            >
              <FormattedMessage id="shopping.page.contactInfo.skipButton" />
            </Button>
          )}
        </CtaContainer>
      </Form>
    </FormProvider>
  );
}

ContactInfoForm.propTypes = {
  defaultValues: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    phone: PropTypes.string
  }),
  onSkip: PropTypes.func,
  onSubmit: PropTypes.func.isRequired
};

export default ContactInfoForm;
